import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_FORMAT } from 'constants/Dates';
import { OpBanner } from 'new-components/DLS/OpBanner/OpBanner';
import { OpFlex } from 'new-components/DLS/OpFlex/OpFlex';
import { OpText } from 'new-components/DLS/OpText/OpText';
import { OpTextButton } from 'new-components/DLS/OpTextButton/OpTextButton';
import { ORG_CANCELLATION_STATUSES } from 'utils/constants';
import { useOpMutation } from 'utils/customHooks/useOpMutation';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { opTime } from 'utils/dates';
import { getOpParams } from 'utils/getOpParams';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';

import './CancellationBanner.scss';

export const CancellationBanner = () => {
  const confirm = useConfirmModal();
  const { orgId } = getOpParams();
  const { t } = useTranslation();

  const { data: cancellations } = useOpQuery({
    apiEndpointName: 'listOrgCancellations',
    parameters: [
      Number(orgId),
      {
        filter: `status:(=${ORG_CANCELLATION_STATUSES.PENDING},=${ORG_CANCELLATION_STATUSES.APPROVED})`,
        limit: 1,
      },
    ],
    select: (data) => data.json.data,
  });

  const { mutateAsync: setOrgCancellationStatus } = useOpMutation({
    apiEndpointName: 'setOrgCancellationStatus',
    queryKeysToInvalidate: [['listCancellations'], ['listOrgCancellations']],
    // useCallback so the linter doesn't complain about unstable nested components
    onErrorMessage: useCallback(
      (error: OP.ApiError) => {
        if (error?.statusCode === 403) {
          return (
            <OpText>
              {t('You are missing the permission')}{' '}
              <strong>{t('allow service cancellation - write')}</strong>{' '}
              {t('. See your administrator.')}
            </OpText>
          );
        }
        return t('Service cancellation status update failed');
      },
      [t],
    ),
  });

  const stopOrgCancellation = async () => {
    await setOrgCancellationStatus({
      apiEndpointRequirements: [orgId, cancellation.id],
      payload: {
        status: ORG_CANCELLATION_STATUSES.STOPPED,
      },
    });
  };

  const [cancellation] = cancellations || [];

  if (!cancellation) return null;

  const { expectedCancellationDate, requestedByUser } = cancellation;

  const formattedDate = opTime(expectedCancellationDate)
    .utc()
    .format(DATE_FORMAT);

  const handleStopCancellation = () => {
    confirm({
      title: t('Stop cancellation?'),
      content: t('This will reverse your decision to cancel your service.'),
      okText: t('Stop cancellation'),
      onOk: async () => {
        try {
          await stopOrgCancellation();
        } catch {
          // Do nothing
        }
      },
    });
  };

  const message = (
    <OpFlex justify="space-between" testId="cancellation-banner">
      <OpText>
        <a
          href={`mailto:${requestedByUser?.identity?.email}`}
          target="_blank"
          rel="noreferrer"
          className="cancellation-banner__requester-name"
        >
          {requestedByUser?.identity?.fullName || 'Unknown'}
        </a>{' '}
        {t(
          'has cancelled service. It will go into effect on {{formattedDate}}.',
          {
            formattedDate,
          },
        )}
      </OpText>

      <OpTextButton onClick={handleStopCancellation}>
        {t('Stop cancellation')}
      </OpTextButton>
    </OpFlex>
  );

  return <OpBanner className="cancellation-banner" message={message} />;
};
